import { useMutation } from '@tanstack/react-query'
import { ActionBar, Modal } from '@viapeople/components'
import { useState } from 'react'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { CycleEmailPossibleGlobalExclusionsInput, CycleEmailPossibleGlobalExclusionUser } from '../types'

interface CycleAddGlobalExclusionsModalProps {
	isOpen: boolean
	onClose: () => void
	cycleId?: string
}

const columns: Column<CycleEmailPossibleGlobalExclusionUser>[] = [
	{
		field: 'firstName',
		label: 'Name',
		colSpan: 1,
		render: (user) => `${user.firstName} ${user.lastName}`,
	},
	{
		field: 'email',
		label: 'email',
		colSpan: 1,
	},
]

export const CycleAddGlobalExclusionsModal = ({ isOpen, onClose, cycleId }: CycleAddGlobalExclusionsModalProps) => {
	const [selectedUsers, setSelectedUsers] = useState<CycleEmailPossibleGlobalExclusionUser[]>([])
	const toast = useToast()
	const { mutate: createCycleEmailsGlobalExclusion, isPending } = useMutation(
		trpc.admin.cycles.emails.globalExclusions.create.mutationOptions({
			onSuccess: () => {
				toast.showToast('Global exclusions added', 'info')
				setSelectedUsers([])
				onClose()
			},
			onError: (error) => {
				toast.showToast(error.message ?? 'Failed to add global exclusions', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.emails.globalExclusions.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const onSubmit = () => {
		createCycleEmailsGlobalExclusion({
			cycleId: cycleId as string,
			userIds: selectedUsers.map((user) => user.id),
		})
	}

	const handleClose = () => {
		if (isPending) return
		setSelectedUsers([])
		onClose()
	}

	return (
		<Modal isOpen={isOpen} fullContent onClose={handleClose} fullScreen title='Add Raters'>
			<div className='flex flex-col h-[calc(100vh-150px)]'>
				<GenericTable<CycleEmailPossibleGlobalExclusionUser, CycleEmailPossibleGlobalExclusionsInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.cycles.emails.globalExclusions.searchPossible.queryOptions({
							...options,
							cycleId: cycleId as string,
						}),
					})}
					isInternal
					selectable
					onSelectionChange={(users) => setSelectedUsers(users)}
					columns={columns}
				/>
			</div>

			<ActionBar
				variant='modal'
				className='left-0 flex justify-end'
				rightActions={[
					{
						label: 'Cancel',
						variant: 'secondary',
						disabled: isPending,
						onClick: (e) => {
							e.preventDefault()
							handleClose()
						},
					},
					{
						label: 'Add Global Exclusions',
						variant: 'primary',
						disabled: isPending || selectedUsers.length === 0,
						isLoading: isPending,
						onClick: (e) => {
							e.preventDefault()
							onSubmit()
						},
					},
				]}
			/>
		</Modal>
	)
}
